/**
 * Participants Modal Component
 * 
 * Modal showing all participants in a group chat with their details.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Table,
  Avatar,
  Tag,
  Typography,
  Spin,
  Button,
  Popconfirm,
  Space,
  App,
} from 'antd';
import { UserOutlined, DeleteOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { Conversation } from '@municipality/types';
import { chatService, useChatStore } from '@municipality/chat';

const { Text } = Typography;

/**
 * Get initials from a name
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a consistent color from a string
 */
const getAvatarColor = (name: string): string => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
    '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

const ParticipantAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== '$bgColor',
})<{ $bgColor?: string }>`
  background-color: ${(props) => props.$bgColor || 'transparent'} !important;
  color: #fff !important;
  font-weight: 500 !important;
`;

interface ParticipantData {
  userId: string;
  name: string;
  surname: string;
  email: string;
  role: string;
  avatar?: string;
  isCreator: boolean;
  isCurrentUser: boolean;
}

interface ParticipantsModalProps {
  open: boolean;
  conversation: Conversation | null;
  currentUserId: string;
  onClose: () => void;
  onRemoveParticipant?: (userId: string) => void;
  canRemoveParticipants?: boolean;
}

export const ParticipantsModal: React.FC<ParticipantsModalProps> = ({
  open,
  conversation,
  currentUserId,
  onClose,
  onRemoveParticipant,
  canRemoveParticipants = true,
}) => {
  const { t } = useTranslation(['chat', 'common']);
  const { t: tAdmin } = useTranslation('admin');
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState<ParticipantData[]>([]);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const removeParticipantFromGroup = useChatStore((state) => state.removeParticipantFromGroup);

  const loadParticipants = useCallback(async () => {
    if (!conversation) return;

    setLoading(true);
    try {
      // Fetch user details (email, surname) for all participants
      const userDetails = await chatService.getUserDetailsBatch(conversation.participantIds);

      // Build participant data array
      const participantData: ParticipantData[] = conversation.participantIds.map((userId) => {
        const participantInfo = conversation.participantDetails[userId];
        const userDetail = userDetails[userId] || { email: '', surname: '', name: participantInfo?.name || 'Unknown' };

        // Prefer role from userDetails (which includes municipalityRole), fallback to participantInfo.role
        const role = userDetail.role || participantInfo?.role || 'user';

        return {
          userId,
          name: userDetail.name || participantInfo?.name || 'Unknown',
          surname: userDetail.surname || '',
          email: userDetail.email || '',
          role: role,
          avatar: participantInfo?.avatar,
          isCreator: conversation.createdBy === userId,
          isCurrentUser: userId === currentUserId,
        };
      });

      // Sort: creator first, then current user, then alphabetically
      participantData.sort((a, b) => {
        if (a.isCreator && !b.isCreator) return -1;
        if (!a.isCreator && b.isCreator) return 1;
        if (a.isCurrentUser && !b.isCurrentUser) return -1;
        if (!a.isCurrentUser && b.isCurrentUser) return 1;
        return a.name.localeCompare(b.name);
      });

      setParticipants(participantData);
    } catch (error) {
      console.error('Failed to load participants:', error);
    } finally {
      setLoading(false);
    }
  }, [conversation, currentUserId]);

  useEffect(() => {
    if (open && conversation && conversation.type === 'group') {
      loadParticipants();
    }
  }, [open, conversation, loadParticipants]);

  const handleRemoveParticipant = async (userId: string) => {
    if (!conversation) return;

    // Validation checks
    if (conversation.createdBy === userId) {
      message.error(t('participantsModal.cannotRemoveCreator'));
      return;
    }

    if (conversation.participantIds.length <= 2) {
      message.error(t('participantsModal.cannotRemoveLast'));
      return;
    }

    setRemovingUserId(userId);
    try {
      await removeParticipantFromGroup(conversation.id, userId, currentUserId);
      message.success(t('participantsModal.removeSuccess') || 'Participant removed successfully');
      
      // Reload participants to reflect changes
      await loadParticipants();
      
      // Call optional callback
      onRemoveParticipant?.(userId);
    } catch (error) {
      console.error('Failed to remove participant:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove participant';
      message.error(errorMessage);
    } finally {
      setRemovingUserId(null);
    }
  };

  const columns = [
    {
      title: '',
      key: 'avatar',
      width: 60,
      render: (_: unknown, record: ParticipantData) => {
        const initials = getInitials(record.name + ' ' + record.surname);
        const color = getAvatarColor(record.name + ' ' + record.surname);
        return (
          <ParticipantAvatar
            size={40}
            src={record.avatar}
            $bgColor={record.avatar ? undefined : color}
            icon={!record.avatar ? <UserOutlined /> : undefined}
          >
            {!record.avatar && initials}
          </ParticipantAvatar>
        );
      },
    },
    {
      title: t('participantsModal.name'),
      key: 'fullName',
      render: (_: unknown, record: ParticipantData) => (
        <Space>
          <Text strong={record.isCurrentUser}>
            {record.name} {record.surname}
          </Text>
          {record.isCreator && (
            <Tag color="blue">{t('participantsModal.creator')}</Tag>
          )}
          {record.isCurrentUser && (
            <Tag color="green">{t('participantsModal.you')}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('participantsModal.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('participantsModal.role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        // Map role values to translation keys
        // Handle both formats: "municipality_admin", "ADMIN", "admin", etc.
        const roleKey = role.toLowerCase().trim();
        let translatedRole: string;
        
        // Check if it's a municipality_admin role (main role field)
        if (roleKey === 'municipality_admin' || roleKey === 'admin') {
          translatedRole = tAdmin('roles.admin');
        } 
        // Check municipality-specific roles (from municipalityRole enum)
        else if (roleKey === 'support') {
          translatedRole = tAdmin('roles.support');
        } 
        else if (roleKey === 'accountant') {
          translatedRole = tAdmin('roles.accountant');
        } 
        else if (roleKey === 'engineer') {
          translatedRole = tAdmin('roles.engineer');
        } 
        else if (roleKey === 'moderator') {
          translatedRole = tAdmin('roles.moderator');
        } 
        else {
          // Fallback to original role if no translation found
          translatedRole = role;
        }
        
        return <Tag>{translatedRole}</Tag>;
      },
    },
    ...(canRemoveParticipants
      ? [
          {
            title: '',
            key: 'actions',
            width: 100,
            render: (_: unknown, record: ParticipantData) => {
              // Don't show remove button for creator
              if (record.isCreator) {
                return null;
              }

              // Can remove others if creator, or remove self
              const canRemove = conversation?.createdBy === currentUserId || record.isCurrentUser;

              if (!canRemove) {
                return null;
              }

              // Use different confirmation message if removing yourself
              const confirmationTitle = record.isCurrentUser
                ? t('participantsModal.removeYourselfConfirmation')
                : t('participantsModal.removeConfirmation', { name: record.name });

              return (
                <Popconfirm
                  title={confirmationTitle}
                  onConfirm={() => handleRemoveParticipant(record.userId)}
                  okText={t('yes', { ns: 'common' })}
                  cancelText={t('no', { ns: 'common' })}
                  okButtonProps={{ danger: true }}
                  disabled={removingUserId === record.userId}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    loading={removingUserId === record.userId}
                    disabled={removingUserId !== null && removingUserId !== record.userId}
                    aria-label={t('participantsModal.removeParticipant', { name: record.name })}
                  />
                </Popconfirm>
              );
            },
          },
        ]
      : []),
  ];

  if (!conversation || conversation.type !== 'group') {
    return null;
  }

  return (
    <Modal
      title={t('participantsModal.title')}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('close', { ns: 'common' })}
        </Button>,
      ]}
      width={900}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={participants}
          rowKey="userId"
          pagination={false}
          size="middle"
        />
      )}
    </Modal>
  );
};

